import { useEffect, useRef, useState } from 'react';

const testimonials = [
  {
    quote: "Before Workflow Wizards, we were manually following up with every lead by hand. Now our entire intake-to-onboarding process runs automatically. We\'ve cut admin time by 70% and our close rate improved significantly because no one falls through the cracks.",
    name: 'Marcus Delgado',
    title: 'Managing Partner',
    company: 'Delgado & Reyes Consulting',
    avatar: 'https://public.readdy.ai/ai/img_res/fb7cd74f4e880ee16170648abc85361c.jpg',
    metric: { before: '12 hrs/week', after: '3 hrs/week', label: 'Admin Time' },
    accent: '#7C3AED',
    industry: 'Consulting',
  },
  {
    quote: "We were losing booked jobs because our dispatch process was a mess of spreadsheets and phone calls. The automated scheduling and routing system they built has completely transformed how we operate. Our crews are always in the right place.",
    name: 'Rachel Kim',
    title: 'Operations Director',
    company: 'ProCare Home Services',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20asian%20female%20business%20leader%20confident%20portrait%20soft%20studio%20lighting%20light%20background%20clean%20minimal%20headshot%20smart%20casual%20attire%2040%20years%20old%20warm%20professional%20smile&width=200&height=200&seq=wwai-tm-02&orientation=squarish',
    metric: { before: '4 missed jobs/month', after: '0 missed jobs', label: 'Dispatch Accuracy' },
    accent: '#14B8A6',
    industry: 'Home Services',
  },
  {
    quote: "Patient intake was taking our front desk 20 minutes per patient. With the automated intake and reminder workflows, patients arrive prepared and our staff focuses on care instead of paperwork. Our no-show rate dropped dramatically.",
    name: 'Dr. James Okafor',
    title: 'Clinic Director',
    company: 'Okafor Wellness Group',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20black%20male%20doctor%20physician%20confident%20portrait%20soft%20studio%20lighting%20white%20background%20clean%20minimal%20headshot%20medical%20coat%2045%20years%20old%20warm%20trustworthy%20expression&width=200&height=200&seq=wwai-tm-03&orientation=squarish',
    metric: { before: '28% no-show rate', after: '7% no-show rate', label: 'Patient Show Rate' },
    accent: '#7C3AED',
    industry: 'Healthcare',
  },
];

const TestimonialsSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="testimonials"
      className="relative py-28 overflow-hidden"
      style={{ background: '#F8FAFC' }}
    >
      {/* Top gradient accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-violet/30 via-brand-teal/30 to-brand-violet/30" />

      {/* Background visual blobs */}
      <div className="absolute top-16 left-0 w-72 h-72 rounded-full bg-brand-violet/4 blur-3xl pointer-events-none" />
      <div className="absolute bottom-16 right-0 w-72 h-72 rounded-full bg-brand-teal/5 blur-3xl pointer-events-none" />

      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className="text-center max-w-2xl mx-auto mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="font-inter text-xs font-semibold text-brand-violet tracking-widest uppercase mb-4 block">
            Client Results
          </span>
          <h2
            className="font-space font-semibold text-brand-text leading-tight mb-5"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)' }}
          >
            Real Businesses.{' '}
            <span className="text-brand-teal">Measurable Results.</span>
          </h2>
          <p className="font-inter text-brand-steel text-base leading-relaxed">
            We don&apos;t just build workflows — we engineer outcomes. Here&apos;s what service businesses say after going live.
          </p>
        </div>

        {/* Testimonial cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className="relative bg-white rounded-2xl p-7 border border-brand-light flex flex-col overflow-hidden group hover:border-opacity-50 transition-all duration-300"
              style={{
                borderColor: 'rgba(226,232,240,0.8)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(32px)',
                transition: `opacity 0.6s ease ${0.1 + i * 0.15}s, transform 0.6s ease ${0.1 + i * 0.15}s`,
              }}
            >
              {/* Top gradient bar */}
              <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl opacity-60"
                style={{ background: `linear-gradient(90deg, ${t.accent}, ${t.accent}60)` }}
              />

              {/* Star rating */}
              <div className="flex items-center gap-1 mb-5">
                {Array.from({ length: 5 }).map((_, si) => (
                  <i key={si} className="ri-star-fill text-sm" style={{ color: '#F59E0B' }} />
                ))}
                <span className="font-inter text-xs text-brand-steel ml-2">{t.industry}</span>
              </div>

              {/* Quote */}
              <div className="relative mb-6 flex-1">
                <i
                  className="ri-double-quotes-l text-4xl absolute -top-1 -left-1 leading-none"
                  style={{ color: `${t.accent}20` }}
                />
                <p className="font-inter text-brand-text text-sm leading-relaxed relative z-10 pl-3">
                  &ldquo;{t.quote}&rdquo;
                </p>
              </div>

              {/* Metric callout */}
              <div
                className="rounded-xl p-4 mb-6 border"
                style={{
                  background: `${t.accent}06`,
                  borderColor: `${t.accent}20`,
                }}
              >
                <p className="font-inter text-[10px] font-bold tracking-widest uppercase mb-2" style={{ color: t.accent }}>
                  {t.metric.label}
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <span className="font-inter text-xs text-brand-steel line-through">{t.metric.before}</span>
                    <span className="font-space font-semibold text-sm" style={{ color: t.accent }}>{t.metric.after}</span>
                  </div>
                  <div className="ml-auto">
                    <i className="ri-arrow-right-line text-sm" style={{ color: t.accent }} />
                  </div>
                </div>
              </div>

              {/* Client info */}
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 border-2" style={{ borderColor: `${t.accent}30` }}>
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-space font-semibold text-brand-text text-sm">{t.name}</p>
                  <p className="font-inter text-brand-steel text-xs">{t.title}, {t.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div
          className="mt-14 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative"
          style={{
            background: 'linear-gradient(135deg, #0f0e2e 0%, #1E1B4B 60%, #2d1b69 100%)',
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 0.7s',
          }}
        >
          {/* Background glow */}
          <div className="absolute top-1/2 left-1/3 -translate-y-1/2 w-48 h-48 rounded-full bg-brand-teal/10 blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <p className="font-space font-semibold text-white text-xl leading-snug mb-1">
              Ready to See Results Like These?
            </p>
            <p className="font-inter text-white/50 text-sm">
              Book a free workflow audit and we&apos;ll identify your top automation opportunities.
            </p>
          </div>

          <div className="relative z-10 flex items-center gap-4 flex-shrink-0">
            <div className="flex -space-x-3">
              {testimonials.map((t) => (
                <div key={t.name} className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#1E1B4B]">
                  <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                </div>
              ))}
            </div>
            <button
              onClick={() => document.querySelector('#hero')?.scrollIntoView({ behavior: 'smooth' })}
              className="whitespace-nowrap font-inter font-semibold text-sm text-brand-indigo px-6 py-3 rounded-lg bg-brand-teal hover:opacity-90 transition-opacity cursor-pointer flex items-center gap-2"
            >
              Book Free Audit
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
